/**
 * Presentation Library Search Agent - Context Engineering Section
 * Section 4: Context Engineering Note
 */

import { PromptSectionBase } from '../../../base/systemPrompt/PromptSectionBase';
import { CONTEXT_ID_PREFIXES } from '../../../base/systemPrompt/shared';
import { IN_PRESENTATION_SEARCH_PROMPT_KEYS } from './variables';

export const IN_PRESENTATION_CONTEXT_PREFIXES: (keyof typeof CONTEXT_ID_PREFIXES)[] = [
    'slide',
    'component',
    'shape',
    'asset',
];

class PresentationLibrarySearchContextNoteSection extends PromptSectionBase {
    protected readonly keywordsKey = IN_PRESENTATION_SEARCH_PROMPT_KEYS.contextNote;
    protected readonly fallbackContent = `## Context Engineering

Context items are passed with specific IDs that you must use correctly to link dynamic content.

### Context ID Prefixes

- **slide_xxx**: Slide data from readSlide (shapes, layout, content)
- **component_xxx**: Component search results from presentations
- **shape_xxx**: Individual shape references from slides
- **asset_xxx**: Presentation assets (embedded images, media)

### Using Context IDs

1. **Reference by ID**: When passing context to sub-agents, use the exact ID
   \`\`\`json
   { "context": ["slide_257", "component_abc12345"], "purpose": "Reference slides and components" }
   \`\`\`

2. **Include purpose**: Always explain what the context is for

3. **Group related context**: Put related items in the same context array

4. **Don't fabricate IDs**: Only use IDs returned from tools/searches

### Result ID Patterns

**Slide IDs:**
- Format: slide_257 or deck_abc123|slide_257
- References a specific slide in a presentation

**Component IDs:**
- Format: component_abc12345
- Globally unique, references grouped visual regions

**Shape IDs:**
- Format: deck_abc123|slide_257|shape_42
- References individual shapes within slides

**Layout IDs:**
- Format: deck_abc123|layout_MasterName
- References master slide templates

### Using Search Results
1. Note the IDs from search results
2. Include relevant IDs in your submitResults call
3. Other agents can retrieve full content using these IDs`;
}

const contextNoteSection = new PresentationLibrarySearchContextNoteSection();

/**
 * Get the context note section.
 * Uses Keywords AI if available, otherwise falls back to code.
 * Optionally injects dynamic deck registry information at runtime.
 */
export function getContextNoteSection(
    hasResolvedContext: boolean = false,
    deckRegistry?: Map<number, { id: string; title: string; slidesReady: number; totalSlides: number }>
): string {
    let section = contextNoteSection.getContent();
    
    // Add deck registry if available (dynamic injection)
    if (deckRegistry && deckRegistry.size > 0) {
        const deckLines = Array.from(deckRegistry.entries())
            .map(([idx, info]) => 
                `  ${idx}: "${info.title}" (${info.slidesReady}/${info.totalSlides} slides ready)`
            )
            .join('\n');
        section += `\n\n## Available Presentations\n${deckLines}\n\nUse documentId for one target ("CURRENT" or index), or documentIds for a specific list. Use documentId: "ALL" only when you need global search across the backend indexed library (which may include decks beyond this tracked list). Prefer targeted documentId/documentIds first.`;
    }
    
    if (hasResolvedContext) {
        section += `\n\n**Note:** You have access to previously resolved context. Review it before searching to avoid redundant queries.`;
    }
    
    return section;
}

/**
 * Export constant for backwards compatibility
 */
export const IN_PRESENTATION_CONTEXT_NOTE = contextNoteSection.getFallbackContent();
